/**
 * ComputePreferencesModal — lets the user review/edit the radius + metrics
 * defaults applied when a new location is added from map search.
 *
 * Follows the same controlled-Dialog pattern as `WorkspaceFormModal`. The
 * draft is local component state, seeded from `useComputePreferences()` only
 * on a closed→open transition so in-progress edits survive unrelated parent
 * re-renders while the modal stays open.
 *
 * The metrics fieldset mirrors `BatchUploadCSVModal`'s captured AADT / demand
 * forecast dependency rule (forecast requires captured AADT), surfaced via a
 * disabled checkbox and the same advisory warning — as a bottom-right toast
 * (`useToastOnNewWarnings`) rather than an inline banner, so it doesn't
 * compete with the modal's own layout. Gated on `open` so a warning already
 * active in the persisted preferences doesn't toast on every map load — only
 * a change made while the modal is open does.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  DIALOG_VIEWPORT_ANCHOR_CLASS,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Label } from '@/components/atoms/label';
import {
  DEFAULT_LOCATION_METRICS,
  METRIC_CHECKBOX_CLASSNAME,
  METRIC_LABEL_CLASSNAME,
  METRIC_LABELS,
  METRICS_LIST,
  type MetricsList,
} from '@/features/locations/constants';
import {
  toggleCapturedAadtSelection,
  toggleForecastSelection,
} from '@/features/locations/utils/metricSelection';
import { radiusIndexToValue, radiusValueToIndex } from '@/features/locations/utils/radiusHelpers';
import {
  MAP_PANEL_CROWDED_BELOW_PX,
  MAP_PREFERENCES_CROWDED_CLASS,
  MAP_PREFERENCES_MODAL_CLASS,
} from '@/features/map/components/mapBarGeometry';
import { RadiusSlider } from '@/features/map/components/RadiusSlider';
import {
  type ComputePreferences,
  useComputePreferences,
} from '@/features/map/hooks/useComputePreferences';
import { useMixpanel } from '@/hooks/useMixpanel';
import { useToastOnNewWarnings } from '@/hooks/useToastOnNewWarnings';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { cn } from '@/libs/utils/cn';

export interface ComputePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Map chrome (`shell.containerRef.current`). Width only: when the map is
   * too narrow the dialog drops under the search bar. The dialog stays
   * viewport-fixed so a tall card cannot push it off the window.
   */
  dialogContainer?: HTMLElement;
}

function mapChromeIsCrowded(container: HTMLElement | undefined): boolean {
  if (!container) return false;
  const width = container.getBoundingClientRect().width;
  return width > 0 && width < MAP_PANEL_CROWDED_BELOW_PX;
}

function useMapChromeCrowded(container: HTMLElement | undefined): boolean {
  const [crowded, setCrowded] = useState(() => mapChromeIsCrowded(container));

  useEffect(() => {
    setCrowded(mapChromeIsCrowded(container));
    if (!container) return;
    const observer = new ResizeObserver(() => setCrowded(mapChromeIsCrowded(container)));
    observer.observe(container);
    return () => observer.disconnect();
  }, [container]);

  return crowded;
}

function metricCheckboxId(metric: MetricsList): string {
  return `compute-preferences-metric-${metric}`;
}

export function ComputePreferencesModal({
  open,
  onOpenChange,
  dialogContainer,
}: ComputePreferencesModalProps) {
  const crowded = useMapChromeCrowded(dialogContainer);
  const { preferences, savePreferences } = useComputePreferences();
  const [draft, setDraft] = useState<ComputePreferences>(preferences);
  const wasOpenRef = useRef(open);
  const trackAction = useMixpanel((s) => s.trackAction);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft({ radiusMeters: preferences.radiusMeters, metrics: [...preferences.metrics] });
    }
    wasOpenRef.current = open;
  }, [open, preferences]);

  const capturedAadtSelected = draft.metrics.includes(METRICS_LIST.CAPTURED_AADT);
  const forecastSelected = draft.metrics.includes(METRICS_LIST.FORECAST);

  // Same stacking, per-metric advisory warnings as BatchUploadCSVModal.
  const metricWarnings: string[] = [];
  if (forecastSelected) {
    metricWarnings.push('Demand forecast requires Captured AADT.');
  }
  if (capturedAadtSelected) {
    metricWarnings.push('Captured AADT slows down compute.');
  }
  useToastOnNewWarnings(metricWarnings, open);

  function toggleSimpleMetric(metric: MetricsList) {
    setDraft((prev) => ({
      ...prev,
      metrics: prev.metrics.includes(metric)
        ? prev.metrics.filter((m) => m !== metric)
        : [...prev.metrics, metric],
    }));
  }

  function applyMetricsChange(next: MetricsList[]) {
    setDraft((prev) => ({ ...prev, metrics: next }));
  }

  function handleRadiusIndexChange(index: number) {
    setDraft((prev) => ({ ...prev, radiusMeters: radiusIndexToValue(index) }));
  }

  function handleSave() {
    savePreferences(draft);
    onOpenChange(false);
    toast.success('Compute preferences saved');
    trackAction(ACTION_TRACKED.COMPUTE_PREFERENCES_SAVED, {
      radius_meters: draft.radiusMeters,
      metrics: draft.metrics,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          MAP_PREFERENCES_MODAL_CLASS,
          DIALOG_VIEWPORT_ANCHOR_CLASS,
          crowded && MAP_PREFERENCES_CROWDED_CLASS,
        )}
      >
        <DialogHeader className="gap-1">
          <DialogTitle className="text-primary-700 text-lg font-normal">
            Compute preferences
          </DialogTitle>
          <DialogDescription>
            Defaults applied when a new location is added from search.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 mb-4">
          <RadiusSlider
            index={radiusValueToIndex(draft.radiusMeters)}
            onIndexChange={handleRadiusIndexChange}
          />
        </div>

        <fieldset className="flex flex-col gap-1.5 border-0 p-0 mb-4">
          <legend className="text-xs font-medium text-primary-700 mb-1">Metrics</legend>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {DEFAULT_LOCATION_METRICS.map((metric) => {
              const id = metricCheckboxId(metric);
              return (
                <div key={metric} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={draft.metrics.includes(metric)}
                    onCheckedChange={() => toggleSimpleMetric(metric)}
                    className={METRIC_CHECKBOX_CLASSNAME}
                  />
                  <Label htmlFor={id} className={METRIC_LABEL_CLASSNAME}>
                    {METRIC_LABELS[metric]}
                  </Label>
                </div>
              );
            })}
            <div className="flex items-center gap-2">
              <Checkbox
                id={metricCheckboxId(METRICS_LIST.CAPTURED_AADT)}
                checked={capturedAadtSelected}
                disabled={forecastSelected}
                onCheckedChange={(checked) =>
                  applyMetricsChange(toggleCapturedAadtSelection(draft.metrics, checked === true))
                }
                className={METRIC_CHECKBOX_CLASSNAME}
              />
              <Label
                htmlFor={metricCheckboxId(METRICS_LIST.CAPTURED_AADT)}
                className={METRIC_LABEL_CLASSNAME}
              >
                {METRIC_LABELS[METRICS_LIST.CAPTURED_AADT]}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id={metricCheckboxId(METRICS_LIST.FORECAST)}
                checked={forecastSelected}
                onCheckedChange={(checked) =>
                  applyMetricsChange(toggleForecastSelection(draft.metrics, checked === true))
                }
                className={METRIC_CHECKBOX_CLASSNAME}
              />
              <Label
                htmlFor={metricCheckboxId(METRICS_LIST.FORECAST)}
                className={METRIC_LABEL_CLASSNAME}
              >
                {METRIC_LABELS[METRICS_LIST.FORECAST]}
              </Label>
            </div>
          </div>
        </fieldset>

        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" size="lg" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
